import { FlexRow, MaterialIcon, StyledButton, useModal } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { OnChangeParams, Value } from 'baseui/select';
import { useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ASSET_TYPE_PHONE } from '../../constants/asset';
import { COMMANDS_STATUS } from '../../constants/commands';
import useEffectOnce from '../../hooks/useEffectOnce';
import { useStyles } from '../../hooks/useStyles';
import { filteredAssetsState } from '../../storage/assets';
import { availableCommandsState } from '../../storage/commands';
import { Asset } from '../../types/asset';
import { InlineSelect } from '../ui/InlineSelect';
import { StyledTooltip } from '../ui/StyledTooltip';
import { useCommands } from './hooks/useCommands';
import CommandModal from './modal/CommandModal';

type Props = {
  selectedAsset?: string;
  selectedCommandType?: string;
  selectedStatus?: string;
  onChangeAsset: (assetId: string) => void;
  onChangeCommandType: (commandType: string) => void;
  onChangeStatus: (status: string) => void;
  onReloadPage: () => void;
};

export function CommandFilters({
  selectedAsset,
  selectedCommandType,
  selectedStatus,
  onChangeAsset,
  onChangeCommandType,
  onChangeStatus,
  onReloadPage,
}: Props) {
  const { css, theme } = useStyles();
  const assets = useRecoilValue(filteredAssetsState);
  const { openModal, closeModal, showModal } = useModal();
  const { getAllAvailableCommands } = useCommands();
  const [availableCommands, setAvailableCommands] = useRecoilState(availableCommandsState);

  useEffectOnce(() => {
    const loadAvailableCommands = async () => {
      const commands = await getAllAvailableCommands();
      if (commands) {
        setAvailableCommands(commands);
      } else {
        setAvailableCommands(undefined);
      }
    };
    loadAvailableCommands();
  });

  const commandTypeOptions = useMemo(() => {
    if (!availableCommands) return;
    return [
      {
        id: '',
        label: 'Todos',
      },
      ...availableCommands.map((availableCommand) => ({
        id: availableCommand.commandName,
        label: availableCommand.commandDescription,
      })),
    ];
  }, [availableCommands]);

  const assetOptions = useMemo(() => {
    if (!assets) return;
    const assetOptionsList = assets.filter((asset) => asset.assetType !== ASSET_TYPE_PHONE);
    return [
      {
        id: '',
        label: 'Todos',
        asset: undefined,
      },
      ...assetOptionsList.map((asset) => ({ id: asset._id, label: asset.label, asset })),
    ];
  }, [assets]);

  const statusOptions = useMemo(() => {
    return [
      {
        id: '',
        label: 'Todos',
      },
      ...Object.keys(COMMANDS_STATUS).map((status) => ({ id: status, label: COMMANDS_STATUS[status] })),
    ];
  }, []);

  const onAssetChange = (params: OnChangeParams) => {
    params.option?.id ? onChangeAsset(params.option.id as string) : onChangeAsset('');
  };

  const onCommandChange = (params: OnChangeParams) => {
    params.option?.id ? onChangeCommandType(params.option.id as string) : onChangeCommandType('');
  };

  const onStatusChange = (params: OnChangeParams) => {
    params.option?.id ? onChangeStatus(params.option.id as string) : onChangeStatus('');
  };

  const onFilterAssets = (options: Value, searchValue: string): Value => {
    if (!options) return options;
    let filteredAssets = [...options];

    if (searchValue) {
      filteredAssets = filteredAssets.filter((option) => {
        if (option.asset) {
          if (
            (option.asset as Asset).label?.toLowerCase().includes(searchValue.toLowerCase()) ||
            (option.asset as Asset).vehicle?.licensePlate?.toLowerCase().includes(searchValue.toLowerCase()) ||
            (option.asset as Asset).container?.identifier?.toLowerCase().includes(searchValue.toLowerCase())
          )
            return true;
        }
        return false;
      });
    }

    return filteredAssets;
  };

  const resetFilters = () => {
    onChangeAsset('');
    onChangeCommandType('');
    onChangeStatus('');
  };

  return (
    <FlexRow
      classNames={css({
        justifyContent: 'space-between',
      })}
    >
      <FlexRow gap={theme.sizing.scale300}>
        {!!assets?.length && (
          <InlineSelect
            label="Asset:"
            options={assetOptions}
            value={[{ id: selectedAsset }]}
            onChange={onAssetChange}
            filterOptions={onFilterAssets}
            searchable
          />
        )}
        {!!availableCommands?.length && (
          <InlineSelect
            label="Comando:"
            options={commandTypeOptions}
            value={[{ id: selectedCommandType }]}
            onChange={onCommandChange}
          />
        )}
        <InlineSelect
          label="Status:"
          options={statusOptions}
          value={[{ id: selectedStatus }]}
          onChange={onStatusChange}
        />
        <StyledTooltip
          content={'Restaurar filtros'}
          showArrow={false}
        >
          <Button
            size="compact"
            shape="square"
            onClick={resetFilters}
            kind="tertiary"
          >
            <MaterialIcon name="recycling" />
          </Button>
        </StyledTooltip>
      </FlexRow>
      <FlexRow gap={theme.sizing.scale300}>
        <StyledTooltip
          content={'Recargar tabla'}
          showArrow={false}
        >
          <Button
            size="compact"
            shape="square"
            onClick={onReloadPage}
            kind="tertiary"
          >
            <MaterialIcon name={'refresh'} />
          </Button>
        </StyledTooltip>
        <StyledButton
          onClick={openModal}
          endEnhancer={
            <MaterialIcon
              name="settings_remote"
              size={theme.sizing.scale600}
              color={theme.colors.contentInversePrimary}
            />
          }
        >
          Enviar Commando
        </StyledButton>
      </FlexRow>
      <CommandModal
        onClose={closeModal}
        isOpen={showModal}
      />
    </FlexRow>
  );
}
